import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  listAdminCommunitiesApi,
  getAdminCommunityApi,
  updateAdminCommunityApi,
  deleteAdminCommunityApi,
  listAdminCommunityCommentsApi,
  deleteAdminCommentApi,
  listAdminTypesApi,
  adminExportUrl,
} from "api/admin";
import {
  C,
  Panel,
  Row,
  SkeletonRows,
  Empty,
  InfoRow,
  DetailFrame,
  SearchBar,
  FilterPills,
  Pagination,
  formatDate,
  relativeTime,
  primaryBtnSm,
  dangerBtnSm,
  ghostBtn,
  inputStyle,
  CsvExportButton,
} from "./admin_ui";

export function AdminCommunitiesList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const search = params.get("q") || "";
  const includeDeleted = params.get("deleted") === "1";
  const page = parseInt(params.get("page") || "1", 10) || 1;

  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => setSearchInput(search), [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await listAdminTypesApi("community");
      if (!cancelled && r?.success) setTypes(r.items || []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminCommunitiesApi({
        type: type || undefined,
        search: search || undefined,
        includeDeleted: includeDeleted ? "1" : undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success) setData({ items: r.items || [], total: r.total || 0 });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [type, search, includeDeleted, page]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            커뮤니티 관리
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            전체 {data.total.toLocaleString()}건 — 부적절한 글은 삭제 또는 수정할 수 있습니다.
          </div>
        </div>
        <CsvExportButton kind="communities" />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <FilterPills
          value={type}
          onChange={(v) => setParam("type", v)}
          options={[
            { label: "전체", value: "" },
            ...types.map((t) => ({ label: t, value: t })),
          ]}
        />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: C.body,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setParam("deleted", e.target.checked ? "1" : "")}
          />
          삭제 글 포함
        </label>
        <div style={{ flex: 1 }} />
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => setParam("q", searchInput.trim())}
          placeholder="제목 / 본문 / 작성자"
        />
      </div>

      <Panel title="게시글 목록" count={data.total}>
        {loading ? (
          <SkeletonRows count={8} />
        ) : error ? (
          <Empty label={error} />
        ) : data.items.length === 0 ? (
          <Empty />
        ) : (
          data.items.map((c) => (
            <Row
              key={c.docId}
              onClick={() => navigate(`/miadmin/communities/${c.docId}`)}
              left={
                <span
                  style={{
                    color: c.status === "-1" ? C.sub : C.ink,
                    textDecoration:
                      c.status === "-1" ? "line-through" : "none",
                  }}
                >
                  {c.title || "—"}
                  {c.status === "-1" && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 6,
                        background: C.red50,
                        color: C.red700,
                      }}
                    >
                      삭제됨
                    </span>
                  )}
                </span>
              }
              mid={`${c.name || "—"} · ${c.type || "—"} · 💬${c.commentCount ?? 0} · ❤${c.likes ?? 0}`}
              right={relativeTime(c.createDate)}
            />
          ))
        )}
        <Pagination
          page={page}
          limit={20}
          total={data.total}
          onChange={(p) => setParam("page", String(p))}
        />
      </Panel>
    </>
  );
}

export function AdminCommunityDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [done, setDone] = useState("");

  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    setError("");
    const r = await getAdminCommunityApi(docId);
    if (r?.success) {
      setData(r.data);
      setEditTitle(r.data?.title || "");
      setEditBody(r.data?.body || "");
    } else {
      setError(r?.error || "조회 실패");
    }
    setLoading(false);
  };

  const reloadComments = async () => {
    setCommentsLoading(true);
    const r = await listAdminCommunityCommentsApi(docId);
    if (r?.success) setComments(r.items || []);
    setCommentsLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await Promise.all([reload(), reloadComments()]);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const c = data || {};
  const isDeleted = String(c.status) === "-1";

  const handleSave = async () => {
    if (!editTitle.trim()) {
      alert("제목을 입력하세요.");
      return;
    }
    setBusy(true);
    setDone("");
    const r = await updateAdminCommunityApi(docId, {
      title: editTitle.trim(),
      body: editBody,
    });
    setBusy(false);
    if (r?.success) {
      setEditing(false);
      setDone("게시글이 수정되었습니다.");
      await reload();
    } else {
      alert(r?.error || "수정 실패");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("이 게시글을 삭제하시겠습니까?")) return;
    setBusy(true);
    const r = await deleteAdminCommunityApi(docId);
    setBusy(false);
    if (r?.success) {
      alert("삭제되었습니다.");
      navigate("/miadmin/communities");
    } else {
      alert(r?.error || "삭제 실패");
    }
  };

  const handleDeleteComment = async (cmtDocId) => {
    if (!window.confirm("이 댓글을 삭제하시겠습니까?")) return;
    const r = await deleteAdminCommentApi(cmtDocId);
    if (r?.success) {
      await reloadComments();
    } else {
      alert(r?.error || "삭제 실패");
    }
  };

  return (
    <>
      <DetailFrame
        title="커뮤니티 글 상세"
        backTo="/miadmin/communities"
        onBack={() => navigate(-1)}
        badge={
          isDeleted ? (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 9999,
                background: C.red50,
                color: C.red700,
              }}
            >
              삭제됨
            </span>
          ) : null
        }
        actions={
          !loading && !error && !isDeleted ? (
            editing ? (
              <span style={{ display: "inline-flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy}
                  style={primaryBtnSm}
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setEditTitle(c.title || "");
                    setEditBody(c.body || "");
                  }}
                  disabled={busy}
                  style={ghostBtn}
                >
                  취소
                </button>
              </span>
            ) : (
              <span style={{ display: "inline-flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  style={primaryBtnSm}
                >
                  수정
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={busy}
                  style={dangerBtnSm}
                >
                  삭제
                </button>
              </span>
            )
          ) : null
        }
      >
        {loading ? (
          <SkeletonRows count={5} />
        ) : error ? (
          <Empty label={error} />
        ) : (
          <>
            {done && (
              <div
                style={{
                  marginBottom: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: C.greenBg,
                  border: `1px solid ${C.greenBorder}`,
                  color: C.greenDark,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {done}
              </div>
            )}
            {editing ? (
              <>
                <div style={{ fontSize: 12, color: C.body, fontWeight: 700 }}>
                  제목
                </div>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={{ ...inputStyle, marginTop: 6 }}
                />
                <div
                  style={{
                    marginTop: 14,
                    fontSize: 12,
                    color: C.body,
                    fontWeight: 700,
                  }}
                >
                  본문
                </div>
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={10}
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${C.line}`,
                    fontSize: 14,
                    color: C.ink,
                    background: "#fff",
                    outline: "none",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    resize: "vertical",
                    lineHeight: 1.6,
                  }}
                />
              </>
            ) : (
              <>
                <InfoRow label="제목" value={c.title} />
                <InfoRow label="카테고리" value={c.type} />
                <InfoRow label="작성자" value={c.name} />
                <InfoRow label="작성자 ID" value={c.id} mono />
                <InfoRow label="등록일" value={formatDate(c.createDate)} />
                <InfoRow label="좋아요" value={c.likes ?? c.likeCount ?? 0} />
                <InfoRow label="댓글 수" value={c.commentCount ?? 0} />
                {isDeleted && c.deleteDate && (
                  <InfoRow
                    label="삭제일"
                    value={
                      <span style={{ color: C.red, fontWeight: 700 }}>
                        {formatDate(c.deleteDate)}
                      </span>
                    }
                  />
                )}
                <div style={{ height: 12 }} />
                <div
                  style={{
                    fontSize: 13,
                    color: C.sub,
                    fontWeight: 700,
                    padding: "8px 0",
                  }}
                >
                  본문
                </div>
                <div
                  style={{
                    padding: "14px 16px",
                    background: C.bg,
                    border: `1px solid ${C.line}`,
                    borderRadius: 10,
                    fontSize: 14,
                    color: C.body,
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    minHeight: 80,
                  }}
                >
                  {c.body || c.content || "—"}
                </div>
              </>
            )}
          </>
        )}
      </DetailFrame>

      {!editing && (
        <Panel title="댓글" count={comments.length}>
          {commentsLoading ? (
            <SkeletonRows count={3} />
          ) : comments.length === 0 ? (
            <Empty label="댓글이 없습니다." />
          ) : (
            comments.map((cm) => (
              <div
                key={cm.docId}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "12px 0",
                  borderBottom: `1px solid ${C.line}`,
                  fontSize: 13,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.sub,
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    {cm.userName || cm.userId} · {relativeTime(cm.createdAt)}
                  </div>
                  <div
                    style={{
                      color: C.ink,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {cm.text || "—"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteComment(cm.docId)}
                  style={{ ...dangerBtnSm, height: 30, fontSize: 11 }}
                >
                  삭제
                </button>
              </div>
            ))
          )}
        </Panel>
      )}
    </>
  );
}
